/**
 * Shared Additional-Question UI — same Yes/No row + explain box used by
 * student-housing Rental Additional Questions and Employment assistance rows.
 * Custom / dynamic Application Form questions reuse these bits.
 */
import type { ReactNode } from 'react'
import { TextareaField } from '#app/components/forms'
import { questionLabel } from '#app/utils/application-detail-v2/display'
import { cn } from '#app/utils/misc'

export const ADDITIONAL_EXPLAIN_PLACEHOLDER =
	'Please Provide Details, Including Dates and Circumstances.'

/** Label left, Yes/No radio pills right — matches rental Additional Questions. */
export function AdditionalYesNoRow({
	name,
	label,
	value,
	error,
	onChange,
	disabled,
	children,
	mobileSingleSelect = false,
}: {
	name: string
	label: string
	value: string
	error?: string
	onChange: (value: 'yes' | 'no') => void
	disabled?: boolean
	children?: ReactNode
	/**
	 * Mobile: one circle on the right. On = Yes, off = No.
	 * `sm+` still shows the Yes / No pills.
	 */
	mobileSingleSelect?: boolean
}) {
	const pillBorder = error ? 'border-[#B33044]!' : 'border-[#CCCCCC]'
	const selected = value === 'yes'
	const shownLabel = questionLabel(label)

	return (
		<div className="space-y-3">
			<div
				aria-invalid={error ? true : undefined}
				className={cn(
					'flex w-full px-[20px] py-[5px] transition-colors',
					mobileSingleSelect
						? 'flex-row items-center justify-between gap-4 sm:gap-8'
						: 'flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-8',
				)}
			>
				<span className="min-w-0 flex-1 text-[18px] font-normal text-[#32302F]">
					{shownLabel}
				</span>

				{mobileSingleSelect ? (
					<button
						type="button"
						aria-pressed={selected}
						aria-label={`${shownLabel}: ${selected ? 'Yes' : 'No'}`}
						disabled={disabled}
						onClick={() => onChange(selected ? 'no' : 'yes')}
						className={cn(
							'flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border sm:hidden',
							selected ? 'border-[#32302F]' : 'border-[#CCCCCC]',
							disabled && 'pointer-events-none opacity-70',
							error && 'border-[#B33044]!',
						)}
					>
						{selected ? (
							<span className="h-2.5 w-2.5 rounded-full bg-[#32302F]" />
						) : null}
					</button>
				) : null}

				<div
					className={cn(
						'shrink-0 items-center gap-2 sm:ml-4 sm:gap-3',
						mobileSingleSelect
							? 'hidden sm:flex'
							: 'mt-2 flex w-full justify-end sm:mt-0 sm:w-auto sm:justify-end',
					)}
				>
					{(['yes', 'no'] as const).map((val) => (
						<label
							key={val}
							className={`flex cursor-pointer items-center gap-2 rounded-[15px] border px-5 py-[10px] text-[14px] font-medium text-[#484848] transition-colors hover:bg-gray-50 md:text-[16px] ${
								disabled ? 'pointer-events-none opacity-70' : ''
							} ${pillBorder}`}
						>
							<input
								type="radio"
								name={name}
								value={val}
								checked={value === val}
								disabled={disabled}
								onChange={() => onChange(val)}
								className="h-4 w-4 cursor-pointer accent-[#32302F]"
							/>
							{val === 'yes' ? 'Yes' : 'No'}
						</label>
					))}
				</div>
			</div>

			{error ? (
				<p className="px-[20px] text-[12px] font-[400] text-[#be123d]">
					{error}
				</p>
			) : null}
			{children ? <div className="px-[20px] pt-1">{children}</div> : null}
		</div>
	)
}

export function AdditionalExplainField({
	id,
	value,
	error,
	onChange,
	disabled,
}: {
	id: string
	value: string
	error?: string[]
	onChange: (value: string) => void
	disabled?: boolean
}) {
	return (
		<div className="animate-in fade-in slide-in-from-top-2 rounded-[35px] bg-white ">
			<TextareaField
				labelProps={{
					htmlFor: id,
					children: 'Please Explain',
					className:
						'absolute -top-2.5 left-4 z-10 bg-white px-1.5 text-[13px] font-medium text-[#32302F] md:text-[14px]',
				}}
				textareaProps={{
					id,
					value,
					disabled,
					placeholder: ADDITIONAL_EXPLAIN_PLACEHOLDER,
					onChange: (e) => onChange(e.target.value),
					rows: 4,
					className: `placeholder:text-[#CCCCCC] placeholder:text-[14px] md:placeholder:text-[16px] placeholder:font-normal min-h-[120px] w-full resize-y rounded-[15px] border bg-[#FBFDFD] px-5 py-5 text-[14px] text-[#484848] md:text-[16px] outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-transparent ${
						error?.length
							? 'border-[#B33044]!'
							: 'border-[#CCCCCC] focus:border-[#CCCCCC] focus-visible:border-[#CCCCCC]'
					}`,
				}}
				errors={error}
				className="relative"
				errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
			/>
		</div>
	)
}

/** Outer card shared by Additional Questions blocks. */
export function AdditionalQuestionsCard({
	title = 'Additional Questions',
	children,
}: {
	title?: string
	children: ReactNode
}) {
	return (
		<div className="rounded-[35px] p-5 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
			<h3 className="mb-4 text-[16px] font-medium text-[#32302F] md:text-[18px] lg:text-[20px]">
				{title}
			</h3>
			<div className="space-y-3">{children}</div>
		</div>
	)
}
